import { isMasqueradeAuth0Config } from "../../config/masqueradeAuth0Config";
import {
  CacheEntry,
  CachePayload,
  DecodedJwt,
  DecodedToken,
  IAuth0Config,
  IMasqueradeAuth0Config,
  IdTokenEntry,
} from "../../interfaces";
import { decodeJwt, urlDecodeB64, verifyIdToken, verifyJwt } from "../jwtUtils";

export const DEFAULT_SCOPE = "openid profile email"; // as defined by auth0 spa sdk

const CACHE_KEY_PREFIX = "@@auth0spajs@@";
const OFFLINE_ACCESS_SCOPE = "offline_access";

// implementation without requiring a polyfill
const dedupe = (arr: string[]) =>
  arr.filter((item, index) => arr.indexOf(item) === index);

/**
 * This is to replicate the scope functionality
 * used in the auth0 spa sdk lib. This scope is used
 * as part of the localstorage key so it must match.
 * @param scopes
 */
export const getUniqueScopes = (...scopes: string[]) => {
  return dedupe(scopes.join(" ").split(/\s+/)).join(" ");
};

/**
 * This matches the key functionality in the
 * auth0 spa sdk lib
 */
export const createAccessTokenKey = (
  clientId: string,
  audience: string,
  scope: string
) => {
  const keyScope = getUniqueScopes(DEFAULT_SCOPE, scope);
  return `${CACHE_KEY_PREFIX}::${clientId}::${audience}::${keyScope}`;
};

/**
 * auth0-spa-js v2 stores the id_token under its own key, independent of
 * audience and scope: `[prefix, clientId, suffix].filter(Boolean).join("::")`.
 * v1 never reads this key, so writing it is harmless there and lets v2 take
 * its fast path instead of falling back to the scoped entry.
 */
export const createIdTokenKey = (clientId: string) =>
  `${CACHE_KEY_PREFIX}::${clientId}::@@user@@`;

/**
 * The scope that goes into the cache key we write, and therefore into the one
 * we read back. `useRefreshTokens` adds `offline_access` so a client
 * configured with refresh tokens can match the entry by containment; see that
 * flag on `IMasqueradeAuth0Config` for why adding it is safe.
 *
 * Used for writes and the scopes required when looking up Auth0 cache entries.
 */
export const getCacheKeyScope = (
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): string =>
  isMasqueradeAuth0Config(auth0Config) && auth0Config.useRefreshTokens
    ? getUniqueScopes(auth0Config.scope, OFFLINE_ACCESS_SCOPE)
    : auth0Config.scope;

const hasMatchingCachedCustomer = (
  key: string,
  accessTokenSub: string
): boolean => {
  try {
    const entry: CachePayload = JSON.parse(window.localStorage.getItem(key)!);
    return decodeJwt(entry.body.access_token).payload.sub === accessTokenSub;
  } catch {
    // An unreadable session cannot safely share the incoming identity.
    return false;
  }
};

/**
 * Remove older entries for this audience and foreign or unreadable sessions
 * across the client. Keep same-customer sessions for other audiences.
 * Auth0 v2 shares one identity per client, so retained tokens must match it.
 */
const discardOtherScopedEntries = (
  auth0Config: IAuth0Config | IMasqueradeAuth0Config,
  keepKey: string,
  accessTokenSub: string
) => {
  const prefix = `${CACHE_KEY_PREFIX}::${auth0Config.clientId}::`;
  Object.keys(window.localStorage)
    .filter((key) => {
      if (!key.startsWith(prefix) || key === keepKey) return false;
      const [, , audience, scope] = key.split("::");
      if (!scope) return false;
      return (
        audience === auth0Config.audience ||
        !hasMatchingCachedCustomer(key, accessTokenSub)
      );
    })
    .forEach((key) => window.localStorage.removeItem(key));
};

/**
 * OIDC protocol claims that auth0-spa-js strips from `user` but keeps in
 * `claims`. Copied verbatim from its jwt.ts, which is byte-identical in
 * v1.10.0 and v2.24.1 - so one implementation serves both.
 */
const OIDC_PROTOCOL_CLAIMS = [
  "iss",
  "aud",
  "exp",
  "nbf",
  "iat",
  "jti",
  "azp",
  "nonce",
  "auth_time",
  "at_hash",
  "c_hash",
  "acr",
  "amr",
  "sub_jwk",
  "cnf",
  "sip_from_tag",
  "sip_date",
  "sip_callid",
  "sip_cseq_num",
  "sip_via_branch",
  "orig",
  "dest",
  "mky",
  "events",
  "toe",
  "txn",
  "rph",
  "sid",
  "vot",
  "vtm",
];

/**
 * Reproduces auth0-spa-js's `decode()` output for an id_token, so a stored
 * entry is indistinguishable from one the SDK wrote itself. `getUser()` reads
 * `user`; `getIdTokenClaims()` reads `claims`.
 */
export const decodeIdToken = (idToken: string): DecodedToken => {
  const payload = JSON.parse(urlDecodeB64(idToken.split(".")[1]));
  const claims: Record<string, any> = { __raw: idToken };
  const user: Record<string, any> = {};
  Object.keys(payload).forEach((k) => {
    claims[k] = payload[k];
    if (!OIDC_PROTOCOL_CLAIMS.includes(k)) {
      user[k] = payload[k];
    }
  });
  return { claims, user };
};

export const wrapCacheEntry = (entry: CacheEntry): CachePayload => {
  const expiresInTime = Math.floor(Date.now() / 1000) + entry.expires_in;
  return {
    body: entry,
    expiresAt: expiresInTime,
  };
};

export const entryNotExpired = (expiresAtSeconds: number): boolean => {
  const leewaySeconds = 60;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return expiresAtSeconds > nowSeconds + leewaySeconds;
};

/**
 * Remove cached ID-token identities that do not match the incoming access token.
 *
 * Auth0 v2 stores identity in a separate `@@user@@` entry keyed only by clientId.
 * When storing an access token without an ID token, this entry may still belong
 * to the previous customer: API calls would use the new customer while
 * `getUser()` returns the old one.
 *
 * Remove the entry if its `sub` differs from the incoming access token's,
 * is missing, or cannot be read. Preserve matching entries so ordinary login
 * keeps the ID token required by v2's `getUser()`.
 */
const discardForeignIdTokenEntry = (
  idTokenKey: string,
  accessTokenSub: unknown
): void => {
  const existing = window.localStorage.getItem(idTokenKey);
  if (!existing) return;

  let sub: unknown;
  try {
    sub = (JSON.parse(existing) as IdTokenEntry).decodedToken?.claims?.sub;
  } catch {
    sub = undefined;
  }

  if (!sub || sub !== accessTokenSub) {
    window.localStorage.removeItem(idTokenKey);
  }
};

/**
 * Find all keys for the same client and audience with the required scopes.
 * Auth0 cache keys may use a different scope order or include extra scopes.
 * Return every match so an unusable entry cannot hide a later valid session.
 */
const findCompatibleCacheKeys = (
  auth0Config: IAuth0Config | IMasqueradeAuth0Config,
  scope: string
): string[] => {
  // Auth0 cache entries may omit profile; openid and the caller's scopes are required.
  const requiredScopes = getUniqueScopes("openid", scope)
    .split(" ")
    .filter(Boolean);
  return Object.keys(window.localStorage).filter((key) => {
    const [prefix, clientId, audience, storedScope] = key.split("::");
    if (
      prefix !== CACHE_KEY_PREFIX ||
      clientId !== auth0Config.clientId ||
      audience !== auth0Config.audience ||
      !storedScope
    )
      return false;

    const storedScopes = new Set(storedScope.split(/\s+/));
    return requiredScopes.every((requiredScope) =>
      storedScopes.has(requiredScope)
    );
  });
};

/**
 * Put the exact package key first, then compatible Auth0 cache keys without duplicates.
 * Both lookup functions use this order; each decides whether an entry is usable.
 */
const getMatchingCacheKeys = (
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): string[] => {
  const scope = getCacheKeyScope(auth0Config);
  const exactKey = createAccessTokenKey(
    auth0Config.clientId,
    auth0Config.audience,
    scope
  );
  return [
    exactKey,
    ...findCompatibleCacheKeys(auth0Config, scope).filter(
      (key) => key !== exactKey
    ),
  ];
};

/**
 * Read the first saved entry, checking the exact key before compatible Auth0 cache keys.
 * Allow expired entries because logout may still need their token for revocation.
 * This lookup does not check expiry or skip malformed JSON. Use findUnexpiredCachedSession
 * when deciding whether an existing session can be reused.
 *
 * Native webviews may have no token in localStorage even after login.
 */
export const getCachedJwtEntry = (
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): CachePayload | undefined => {
  for (const key of getMatchingCacheKeys(auth0Config)) {
    const cacheEntryString = window.localStorage.getItem(key);
    if (cacheEntryString) return JSON.parse(cacheEntryString);
  }
  return undefined;
};

/** Check claim shapes and expiry only; callers still verify the token's signature. */
const hasReadableUnexpiredClaims = ({ payload }: DecodedJwt): boolean =>
  payload != null &&
  typeof payload === "object" &&
  !Array.isArray(payload) &&
  (payload.scope === undefined || typeof payload.scope === "string") &&
  (payload.sub === undefined || typeof payload.sub === "string") &&
  Number.isFinite(payload.exp) &&
  entryNotExpired(payload.exp);

/**
 * Check each matching entry until an unexpired, readable session is found.
 * Check both cache expiry and JWT expiry, and the claim shapes used by session decisions.
 * Skip expired or unreadable entries so they cannot hide a later valid session.
 * Do not delete or rewrite entries; preserving a session also preserves its refresh token.
 * Read access-token-only entries that Auth0 v2 may not recognise as signed-in sessions.
 * Callers must verify the returned token before reusing it. This does not renew tokens.
 */
export const findUnexpiredCachedSession = (
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): { accessToken: string; decoded: DecodedJwt } | undefined => {
  for (const key of getMatchingCacheKeys(auth0Config)) {
    try {
      const storedEntry = window.localStorage.getItem(key);
      const entry: CachePayload | null = storedEntry
        ? JSON.parse(storedEntry)
        : null;
      // JSON.parse does not validate types; expiry must be a finite number.
      if (
        entry &&
        Number.isFinite(entry.expiresAt) &&
        entryNotExpired(entry.expiresAt)
      ) {
        const accessToken = entry.body.access_token;
        // Decode only non-empty strings; cached token values may have the wrong type.
        const decoded =
          typeof accessToken === "string" && accessToken
            ? decodeJwt(accessToken)
            : undefined;
        if (decoded && hasReadableUnexpiredClaims(decoded))
          return { accessToken, decoded };
      }
    } catch {
      // A stale or unreadable entry must not hide another matching session.
    }
  }
  return undefined;
};

interface VerifiedTokenPair {
  accessToken: string;
  decoded: DecodedJwt;
  idToken?: string;
  decodedToken?: DecodedToken;
}

const verifyTokenPair = async (
  jwt: string,
  auth0Config: IAuth0Config | IMasqueradeAuth0Config,
  idToken?: string,
  idTokenAudience?: string
): Promise<VerifiedTokenPair> => {
  const decoded = decodeJwt(jwt);
  await verifyJwt(jwt, decoded, auth0Config);

  let decodedToken: DecodedToken | undefined;
  if (idToken) {
    await verifyIdToken(
      idToken,
      decodeJwt(idToken),
      auth0Config,
      idTokenAudience
    );
    decodedToken = decodeIdToken(idToken);

    // Genuine tokens must also belong to the same user. Keep user identifiers
    // out of the error because it can reach the logger.
    if (decodedToken.claims.sub !== decoded.payload.sub) {
      throw new Error(
        "Subject (sub) mismatch between the access token and the id token; they describe different users"
      );
    }
  }

  return { accessToken: jwt, decoded, idToken, decodedToken };
};

const buildCacheEntry = (
  tokenPair: VerifiedTokenPair,
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): CacheEntry => ({
  access_token: tokenPair.accessToken,
  expires_in: Math.floor(tokenPair.decoded.payload.exp - Date.now() / 1000),
  audience: auth0Config.audience,
  scope: getUniqueScopes(DEFAULT_SCOPE, getCacheKeyScope(auth0Config)),
  client_id: auth0Config.clientId,
  ...(tokenPair.idToken
    ? { id_token: tokenPair.idToken, decodedToken: tokenPair.decodedToken }
    : {}),
});

/** Clears incompatible client sessions before writing new credentials. */
const clearForeignAndStaleEntries = (
  tokenPair: VerifiedTokenPair,
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): void => {
  const accessTokenSub = tokenPair.decoded.payload.sub;
  discardForeignIdTokenEntry(
    createIdTokenKey(auth0Config.clientId),
    accessTokenSub
  );
  discardOtherScopedEntries(
    auth0Config,
    createAccessTokenKey(
      auth0Config.clientId,
      auth0Config.audience,
      getCacheKeyScope(auth0Config)
    ),
    accessTokenSub
  );
};

/** Stores the scoped access-token entry. */
const storeAccessToken = (
  tokenPair: VerifiedTokenPair,
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): void => {
  const cacheEntry = buildCacheEntry(tokenPair, auth0Config);
  const payload = wrapCacheEntry(cacheEntry);

  const accessTokenKey = createAccessTokenKey(
    auth0Config.clientId,
    auth0Config.audience,
    getCacheKeyScope(auth0Config)
  );

  window.localStorage.setItem(accessTokenKey, JSON.stringify(payload));
};

/** Stores the v2 identity entry when an incoming ID token is supplied. */
const storeIdToken = (
  tokenPair: VerifiedTokenPair,
  auth0Config: IAuth0Config | IMasqueradeAuth0Config
): void => {
  const idTokenKey = createIdTokenKey(auth0Config.clientId);

  if (tokenPair.idToken && tokenPair.decodedToken) {
    const idTokenEntry: IdTokenEntry = {
      id_token: tokenPair.idToken,
      decodedToken: tokenPair.decodedToken,
    };
    window.localStorage.setItem(idTokenKey, JSON.stringify(idTokenEntry));
  }
};

/**
 * Verifies the token pair (access_token, id_token) before changing the Auth0 SPA cache.
 * Clears foreign sessions across the client before replacing its shared identity.
 * Stores ID tokens in both v1 and v2 formats, preserving matching cached identities
 * when no incoming ID token is supplied.
 *
 * @param jwt the access token
 * @param auth0Config config for token verification, client ID, audience, and cache scopes
 * @param idToken optional ID token for the same user
 * @param idTokenAudience expected minting client; defaults to auth0Config.clientId
 */
export const storeJwt = async (
  jwt: string,
  auth0Config: IAuth0Config | IMasqueradeAuth0Config,
  idToken?: string,
  idTokenAudience?: string
): Promise<void> => {
  const tokenPair = await verifyTokenPair(
    jwt,
    auth0Config,
    idToken,
    idTokenAudience
  );

  clearForeignAndStaleEntries(tokenPair, auth0Config);

  storeAccessToken(tokenPair, auth0Config);
  storeIdToken(tokenPair, auth0Config);
};
